import { Avatar, Button } from "antd";
import { Check, LockKeyhole, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useAppleValidateMerchant,
  useCreateAdhocMobilePayment,
} from "../../api/api.post";
import { useMessage } from "../../components/notifications/message";
import { capitalize } from "../../utils/functions";
import { toFixed, toFixedDisplay } from "../../utils/number.utils";
import { useGetStoreDetailsForOnlineOrder } from "../../api/api.get";

export default function AdhocMobilePaymentsPage() {
  const [searchParams] = useSearchParams();

  const amountParam = new URLSearchParams(searchParams).get("amount");
  const paymentTypeParam = new URLSearchParams(searchParams).get("type");
  const taxParam = new URLSearchParams(searchParams).get("tax");

  const { id } = useParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const { data: storeDetailsData, error } =
    useGetStoreDetailsForOnlineOrder(id);

  const { mutateAsync: createPaymentAsync } = useCreateAdhocMobilePayment(id);

  const paymentType = useMemo(() => {
    if (paymentTypeParam) {
      if (paymentTypeParam.toLowerCase() === "apple_pay") {
        return "apple";
      }
      if (paymentTypeParam.toLowerCase() === "card") {
        return "card";
      }
    }

    return undefined;
  }, [paymentTypeParam]);

  const { messageError, messageSuccess, notification } = useMessage();
  const { mutateAsync: validateAppleMerchantAsync } =
    useAppleValidateMerchant(id);

  const netTotal = useMemo(
    () => (amountParam ? toFixed(amountParam) : 0),
    [amountParam],
  );
  const taxAmount = useMemo(() => {
    let taxPercent = 0;
    if (taxParam) {
      taxPercent = +taxParam;
    } else {
      taxPercent = storeDetailsData?.tax ?? 0;
    }
    return toFixed((taxPercent / 100) * netTotal);
  }, [taxParam, storeDetailsData, netTotal]);

  const grossTotal = toFixed(netTotal + taxAmount);

  const handleApplePay = async () => {
    console.log("Handle apple pay start1");
    if (!window.PaymentRequest) {
      alert("Apple Pay not supported in this browser");
      return;
    }

    const supportedInstruments: PaymentMethodData[] = [
      {
        supportedMethods: "https://apple.com/apple-pay",
        data: {
          version: 3,
          merchantIdentifier: "merchant.expensehut.com",
          merchantCapabilities: ["supports3DS"],
          supportedNetworks: ["visa", "masterCard", "amex", "discover"],
          countryCode: "US",
          currencyCode: "USD",
        },
      },
    ];

    const details: PaymentDetailsInit = {
      total: {
        label: "ExpenseHut",
        amount: { currency: "USD", value: toFixedDisplay(grossTotal) },
      },
    };

    try {
      console.log("Start payment request");
      const request = new PaymentRequest(supportedInstruments, details);

      request.addEventListener("merchantvalidation", (event: any) => {
        console.log("Merchant validation start", event);
        validateAppleMerchantAsync({ validationUrl: "" })
          .then((res) => {
            event.complete(res);
          })
          .catch(() => {
            messageError("Whoops! Let's try again.");
          });
      });
      console.log("Open payment sheet");

      // Show Apple Pay sheet
      const paymentResponse = await request.show();

      console.log("Done paymentsheet");
      // You would send this to your backend to tokenize with Fiserv/CardPointe
      console.log("Apple Pay response:", paymentResponse);

      // Mark success
      await paymentResponse.complete("success");
      console.log("Payment is full simulated");

      await createPaymentAsync({
        applePaymentData: paymentResponse,
        amount: grossTotal,
        createdBy: "abcde",
        type: "APPLE",
      });
      messageSuccess("Payment success");
      setPaymentSuccess(true);
    } catch (err: any) {
      if (err?.name === "AbortError") {
        // do nothing. User has cancelled the payment
      } else {
        console.error("Apple Pay Payment failed", err);
        messageError(err.message ?? "Payment Failed!");
      }
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 pb-32"
      style={{ background: storeDetailsData?.bodyBackgroundColor }}
    >
      {notification}

      {!!error && (
        <div className="flex flex-col gap-3 justify-center my-10 items-center">
          <Avatar src="/assets/logo.png" className="w-[60px] h-[60px]" />

          <div className="flex items-center px-2 py-2 gap-3">
            <TriangleAlert className="border-red-300 text-red-500 font-bold" />

            <p className="text-red-500 font-bold">
              Merchant not found or not active.
            </p>
          </div>
        </div>
      )}
      {/* Merchant Header */}

      {!error && (
        <div>
          <div className="bg-white border-b border-gray-200 p-4 md:rounded-t-lg">
            <p className="text-xs text-gray-500 font-semibold tracking-wider mb-3">
              PAYING TO
            </p>

            {/* Logo and Info Container */}
            <div className="flex gap-4">
              {/* Logo */}
              <div className="flex-shrink-0">
                <img
                  src={
                    storeDetailsData?.logo ?? "/assets/images/generic-logo.svg"
                  }
                  alt={storeDetailsData?.name}
                  className="w-20 h-20 object-cover rounded-lg shadow-sm border border-gray-200"
                />
              </div>

              {/* Merchant Info */}
              <div className="flex-1">
                {/* Restaurant Name */}
                <h1 className="text-lg font-bold text-gray-900 mb-0.5">
                  {capitalize(storeDetailsData?.name)}
                </h1>

                {/* Address - Compact */}
                <div className="text-xs text-gray-600 space-y-0.5">
                  <p>{storeDetailsData?.street1}</p>
                  {storeDetailsData?.street2 && (
                    <p>{storeDetailsData.street2}</p>
                  )}
                  <p>
                    {storeDetailsData?.city}, {storeDetailsData?.state}{" "}
                    {storeDetailsData?.zipCode}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}

          {!paymentSuccess && (
            <div>
              <div className="p-4 space-y-4">
                {/* Payment Summary */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Net Total</span>
                    <span className="text-md font-semibold text-gray-900">
                      ${toFixedDisplay(netTotal)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-md text-gray-600">
                      Tax ({toFixed(taxParam ?? 0)}%)
                    </span>
                    <span className="text-md font-semibold text-gray-900">
                      ${toFixedDisplay(taxAmount)}
                    </span>
                  </div>

                  <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900">
                      Gross Total
                    </span>
                    <span className="text-3xl font-bold text-gray-600">
                      ${toFixedDisplay(grossTotal)}
                    </span>
                  </div>
                </div>

                {/* Payment Method Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="flex gap-2 text-sm text-blue-900 font-medium">
                    <LockKeyhole />
                    Payment will be processed securely
                  </p>
                </div>
              </div>
              {/* Fixed Bottom Pay Button */}
              <div className="mt-10 bg-white border-t border-gray-200 p-4 flex justify-center items-center">
                {paymentType === "apple" && (
                  <Button
                    style={{
                      backgroundColor: "black",
                      color: "white",
                      fontSize: "18px",
                      padding: "12px 20px",
                      borderRadius: "8px",
                      height: 40,
                    }}
                    onClick={handleApplePay}
                  >
                    Pay with  Apple Pay
                  </Button>
                )}
                {!paymentType && (
                  <div className="border-red-300 text-red-500 p-4 flex gap-2 font-bold">
                    <TriangleAlert />
                    Payment type unspecified. Please contact the merchant.
                  </div>
                )}
              </div>
            </div>
          )}

          {paymentSuccess && (
            <div className=" bg-gradient-to-b from-green-50 to-green-100 flex justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-sm">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Payment Successful!
                </h1>
                <p className="text-gray-600 mb-6">
                  Thank you for your payment of ${toFixedDisplay(grossTotal)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
